// Per-symbol ticker logos for the `/tradable-assets` table. Each
// logo is a tiny inline SVG (~80–200 bytes after gzip) using brand-
// recognizable colors but no licensed wordmarks — these are
// stylized representations, not the issuers' trademarked logos.
//
// All 24×24 viewBox; default `size = 18` matching the table cell
// density; `aria-hidden="true"` because the row's symbol cell
// already conveys identity to screen readers (lesson L-159).

interface TickerProps {
  size?: number;
  className?: string;
}

const FONT = "Space Grotesk, system-ui, sans-serif";

// --- FX pairs: side-by-side mini-flags ---

function FlagEU(): JSX.Element {
  return (
    <g>
      <rect width="11" height="14" rx="1.5" fill="#003399" />
      <circle cx="5.5" cy="7" r="2.4" fill="none" stroke="#FFCC00" strokeWidth="0.7" strokeDasharray="0.6 0.7" />
    </g>
  );
}

function FlagUS(): JSX.Element {
  return (
    <g>
      <rect width="11" height="14" rx="1.5" fill="#fff" />
      <path d="M0 2h11M0 4h11M0 6h11M0 8h11M0 10h11M0 12h11" stroke="#B22234" strokeWidth="1.1" />
      <rect width="5.5" height="7" rx="1.5" fill="#3C3B6E" />
    </g>
  );
}

function FlagGB(): JSX.Element {
  return (
    <g>
      <rect width="11" height="14" rx="1.5" fill="#012169" />
      <path d="M0 0L11 14M11 0L0 14" stroke="#fff" strokeWidth="1.6" />
      <path d="M5.5 0v14M0 7h11" stroke="#fff" strokeWidth="2.4" />
      <path d="M5.5 0v14M0 7h11" stroke="#C8102E" strokeWidth="1.4" />
    </g>
  );
}

function FlagJP(): JSX.Element {
  return (
    <g>
      <rect width="11" height="14" rx="1.5" fill="#fff" stroke="#ddd" strokeWidth="0.4" />
      <circle cx="5.5" cy="7" r="3" fill="#BC002D" />
    </g>
  );
}

function PairLogo({
  size,
  className,
  Left,
  Right,
}: {
  size: number;
  className?: string | undefined;
  Left: () => JSX.Element;
  Right: () => JSX.Element;
}): JSX.Element {
  return (
    <svg
      width={size * 1.4}
      height={size}
      viewBox="0 0 33 18"
      className={className}
      aria-hidden="true"
    >
      <g transform="translate(1 2)">
        <Left />
      </g>
      <g transform="translate(13 2)">
        <Right />
      </g>
    </svg>
  );
}

export function EurUsdLogo({ size = 18, className }: TickerProps): JSX.Element {
  return <PairLogo size={size} className={className} Left={FlagEU} Right={FlagUS} />;
}

export function GbpUsdLogo({ size = 18, className }: TickerProps): JSX.Element {
  return <PairLogo size={size} className={className} Left={FlagGB} Right={FlagUS} />;
}

export function UsdJpyLogo({ size = 18, className }: TickerProps): JSX.Element {
  return <PairLogo size={size} className={className} Left={FlagUS} Right={FlagJP} />;
}

// --- Crypto ---

export function BtcLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#F7931A" />
      <text x="12" y="17" textAnchor="middle" fontFamily={FONT} fontSize="15" fontWeight="700" fill="#fff">
        ₿
      </text>
    </svg>
  );
}

export function EthLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#627EEA" />
      <path d="M12 4l5 8.2-5 2.9-5-2.9z" fill="#fff" fillOpacity="0.9" />
      <path d="M12 4L7 12.2 12 7.7z" fill="#fff" fillOpacity="0.7" />
      <path d="M12 16l5-2.9-5 7.4-5-7.4z" fill="#fff" fillOpacity="0.85" />
    </svg>
  );
}

export function XrpLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="#23292F" />
      <path
        d="M7.2 7.5c1.7 2.4 4.2 4 4.8 4 .6 0 3.1-1.6 4.8-4M7.2 16.5c1.7-2.4 4.2-4 4.8-4 .6 0 3.1 1.6 4.8 4"
        stroke="#fff"
        strokeWidth="1.4"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

// --- Metals ---

export function XauLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M4 16l3-6h10l3 6z" fill="#D4AF37" stroke="#A6862C" strokeWidth="0.6" />
      <text x="12" y="15" textAnchor="middle" fontFamily={FONT} fontSize="6.5" fontWeight="700" fill="#3a2e0a">
        AU
      </text>
    </svg>
  );
}

export function XagLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M4 16l3-6h10l3 6z" fill="#C0C0C0" stroke="#888" strokeWidth="0.6" />
      <text x="12" y="15" textAnchor="middle" fontFamily={FONT} fontSize="6.5" fontWeight="700" fill="#2a2a2a">
        AG
      </text>
    </svg>
  );
}

// --- Indices ---

export function Us100Logo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2.5" fill="#0080C8" />
      <text x="12" y="15.5" textAnchor="middle" fontFamily={FONT} fontSize="7" fontWeight="700" fill="#fff">
        100
      </text>
    </svg>
  );
}

export function Spx500Logo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2.5" fill="#D52B1E" />
      <text x="12" y="15.5" textAnchor="middle" fontFamily={FONT} fontSize="6.5" fontWeight="700" fill="#fff">
        S&amp;P
      </text>
    </svg>
  );
}

// --- Energies ---

function OilDrop({ fill, label }: { fill: string; label: string }): JSX.Element {
  return (
    <svg width="100%" height="100%" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3.5c3 4 5.5 6.5 5.5 10A5.5 5.5 0 0112 19a5.5 5.5 0 01-5.5-5.5C6.5 10 9 7.5 12 3.5z"
        fill={fill}
      />
      <text x="12" y="16" textAnchor="middle" fontFamily={FONT} fontSize="5.5" fontWeight="700" fill="#fff">
        {label}
      </text>
    </svg>
  );
}

export function WtiLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <span style={{ display: "inline-block", width: size, height: size }} className={className}>
      <OilDrop fill="#1f1f1f" label="WTI" />
    </span>
  );
}

export function BrentLogo({ size = 18, className }: TickerProps): JSX.Element {
  return (
    <span style={{ display: "inline-block", width: size, height: size }} className={className}>
      <OilDrop fill="#0b3d2e" label="BR" />
    </span>
  );
}
